/**
 * Warehouses & locations admin — `/inventory/warehouses`.
 *
 * Master-detail: the warehouse list on the logical start side, the selected
 * warehouse's bins on the end side. Both panes are cursor lists. Every write
 * carries what the contract demands: an Idempotency-Key on POST, a
 * round-tripped ETag as `If-Match` on PATCH/DELETE, and
 * `application/merge-patch+json` on both PATCH bodies.
 *
 * Neither delete is destructive — the warehouse DELETE archives and the
 * location DELETE inactivates — and both publish typed refusals
 * (`warehouse_has_movements`, `location_has_stock`) that are explained here
 * rather than dropped into a generic toast.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Archive, Building2, Pencil, Plus } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { ConflictDialog, surfaceProblem } from "@/components/form";
import { DataTable, badgeCell, textCell } from "@/components/data-table";
import { ApiError } from "@/lib/api/errors";
import { isPreconditionFailed, useIdempotentMutation } from "@/features/shared/ops-api";
import { localizedName } from "@/lib/i18n";

import {
  LOCATION_STATUSES,
  LOCATION_TYPES,
  WAREHOUSE_STATUSES,
  archiveWarehouse,
  createLocation,
  createWarehouse,
  deactivateLocation,
  getLocation,
  updateLocation,
  updateWarehouse,
  type Warehouse,
  type WarehouseLocation,
  type WarehouseLocationStatus,
  type WarehouseLocationType,
  type WarehouseStatus,
} from "../api";
import {
  asLocationHasStockProblem,
  asWarehouseHasMovementsProblem,
  isDefaultLocationInUse,
  isDuplicateCode,
  type LocationHasStockProblem,
  type WarehouseHasMovementsProblem,
} from "../problems";
import {
  inventoryKeys,
  useLocationList,
  useWarehouseDetail,
  useWarehouseList,
  type LocationListFilters,
  type WarehouseListFilters,
} from "../queries";
import { ALL, BranchSelect, LocationSelect } from "../components/pickers";

interface WarehouseDraft {
  code: string;
  name: string;
  locationsEnabled: boolean;
  branchId: string | null;
  defaultLocationCode: string;
  defaultLocationName: string;
  defaultLocationType: WarehouseLocationType;
}

const EMPTY_WAREHOUSE: WarehouseDraft = {
  code: "",
  name: "",
  locationsEnabled: true,
  branchId: null,
  defaultLocationCode: "",
  defaultLocationName: "",
  defaultLocationType: "receiving",
};

interface LocationDraft {
  code: string;
  name: string;
  locationType: WarehouseLocationType;
  status: WarehouseLocationStatus;
}

const EMPTY_LOCATION: LocationDraft = {
  code: "",
  name: "",
  locationType: "pick",
  status: "active",
};

export interface WarehousesPageProps {
  /** `?warehouse=` deep link (nav from a movement or a location refusal). */
  initialWarehouseId?: string;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  canReadBranches?: boolean;
  /** Acting branch from the shell — the create form's default owner. */
  actingBranchId?: string | null;
  /** `location_has_stock` recovery: open the stock browser filtered to the bin. */
  onOpenStock?: (filters: { warehouseId: string; locationId: string }) => void;
  pageLimit?: number;
}

export function WarehousesPage({
  initialWarehouseId,
  canCreate = false,
  canUpdate = false,
  canDelete = false,
  canReadBranches = false,
  actingBranchId = null,
  onOpenStock,
  pageLimit,
}: WarehousesPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<WarehouseStatus | typeof ALL>(ALL);
  const [selectedId, setSelectedId] = useState<string | null>(initialWarehouseId ?? null);
  const [creating, setCreating] = useState(false);
  const [warehouseDraft, setWarehouseDraft] = useState<WarehouseDraft>(EMPTY_WAREHOUSE);
  const [warehouseFormError, setWarehouseFormError] = useState<string | null>(null);
  const [archiveRefusal, setArchiveRefusal] = useState<WarehouseHasMovementsProblem | null>(null);
  const [conflict, setConflict] = useState(false);

  const [locationTypeFilter, setLocationTypeFilter] = useState<WarehouseLocationType | typeof ALL>(
    ALL,
  );
  const [locationStatusFilter, setLocationStatusFilter] = useState<
    WarehouseLocationStatus | typeof ALL
  >(ALL);
  const [locationDraft, setLocationDraft] = useState<LocationDraft>(EMPTY_LOCATION);
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [editingLocationEtag, setEditingLocationEtag] = useState<string | null>(null);
  const [locationFormError, setLocationFormError] = useState<string | null>(null);
  const [locationRefusal, setLocationRefusal] = useState<LocationHasStockProblem | null>(null);

  useEffect(() => {
    if (initialWarehouseId !== undefined) setSelectedId(initialWarehouseId);
  }, [initialWarehouseId]);

  const listFilters = useMemo<WarehouseListFilters>(() => {
    const built: WarehouseListFilters = {};
    if (search !== "") built.q = search;
    if (status !== ALL) built.status = status;
    return built;
  }, [search, status]);

  const warehousesQuery = useWarehouseList(listFilters, pageLimit);
  const detailQuery = useWarehouseDetail(selectedId);
  const warehouse = detailQuery.data?.data ?? null;
  const warehouseEtag = detailQuery.data?.etag ?? null;

  const locationFilters = useMemo<LocationListFilters>(() => {
    const built: LocationListFilters = {};
    if (locationTypeFilter !== ALL) built.location_type = locationTypeFilter;
    if (locationStatusFilter !== ALL) built.status = locationStatusFilter;
    return built;
  }, [locationTypeFilter, locationStatusFilter]);

  const locationsQuery = useLocationList(selectedId, locationFilters, pageLimit);

  const invalidateWarehouses = () => {
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.warehouses.all });
  };

  const handleWriteError = (error: unknown) => {
    if (isPreconditionFailed(error)) {
      setConflict(true);
      return true;
    }
    return false;
  };

  // ── Warehouse writes ───────────────────────────────────────────────────

  const createWarehouseMutation = useIdempotentMutation({
    mutationFn: (draft: WarehouseDraft, key: string) =>
      createWarehouse(
        {
          branch_id: draft.branchId as string,
          code: draft.code,
          name: draft.name,
          locations_enabled: draft.locationsEnabled,
          default_location: {
            code: draft.defaultLocationCode,
            name: draft.defaultLocationName,
            location_type: draft.defaultLocationType,
          },
        },
        key,
      ),
    onSuccess: (created) => {
      setCreating(false);
      setWarehouseDraft(EMPTY_WAREHOUSE);
      setWarehouseFormError(null);
      setSelectedId(created.id);
      invalidateWarehouses();
    },
    onError: (error) => {
      if (isDuplicateCode(error)) {
        setWarehouseFormError(
          t("inventory.warehouses.duplicateCode", "That warehouse code is already in use."),
        );
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const updateWarehouseMutation = useMutation({
    mutationFn: (input: {
      warehouseId: string;
      patch: Parameters<typeof updateWarehouse>[1];
      ifMatch: string;
    }) => updateWarehouse(input.warehouseId, input.patch, input.ifMatch),
    onSuccess: () => {
      setWarehouseFormError(null);
      invalidateWarehouses();
    },
    onError: (error) => {
      if (handleWriteError(error)) return;
      if (isDefaultLocationInUse(error)) {
        setWarehouseFormError(
          t(
            "inventory.warehouses.defaultLocationInUse",
            "That bin is still the warehouse default and cannot be retired.",
          ),
        );
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const archiveWarehouseMutation = useMutation({
    mutationFn: (input: { warehouseId: string; ifMatch: string }) =>
      archiveWarehouse(input.warehouseId, input.ifMatch),
    onSuccess: () => {
      setArchiveRefusal(null);
      invalidateWarehouses();
    },
    onError: (error) => {
      if (handleWriteError(error)) return;
      const refusal = asWarehouseHasMovementsProblem(error);
      if (refusal !== null) {
        setArchiveRefusal(refusal);
        return;
      }
      surfaceProblem(error, t);
    },
  });

  // ── Location writes ────────────────────────────────────────────────────

  const resetLocationForm = () => {
    setEditingLocationId(null);
    setEditingLocationEtag(null);
    setLocationDraft(EMPTY_LOCATION);
    setLocationFormError(null);
  };

  const loadLocationMutation = useMutation({
    mutationFn: (input: { warehouseId: string; locationId: string }) =>
      getLocation(input.warehouseId, input.locationId),
    onSuccess: ({ data, etag }) => {
      setEditingLocationId(data.id);
      setEditingLocationEtag(etag);
      setLocationDraft({
        code: data.code,
        name: data.name,
        locationType: data.location_type,
        status: data.status,
      });
      setLocationFormError(null);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const createLocationMutation = useIdempotentMutation({
    mutationFn: (input: { warehouseId: string; draft: LocationDraft }, key: string) =>
      createLocation(
        input.warehouseId,
        {
          code: input.draft.code,
          name: input.draft.name,
          location_type: input.draft.locationType,
        },
        key,
      ),
    onSuccess: () => {
      resetLocationForm();
      invalidateWarehouses();
    },
    onError: (error) => {
      if (isDuplicateCode(error)) {
        setLocationFormError(
          t("inventory.warehouses.duplicateBinCode", "That bin code already exists here."),
        );
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const updateLocationMutation = useMutation({
    mutationFn: (input: {
      warehouseId: string;
      locationId: string;
      patch: Parameters<typeof updateLocation>[2];
      ifMatch: string;
    }) => updateLocation(input.warehouseId, input.locationId, input.patch, input.ifMatch),
    onSuccess: () => {
      resetLocationForm();
      invalidateWarehouses();
    },
    onError: (error) => {
      if (handleWriteError(error)) return;
      const refusal = asLocationHasStockProblem(error);
      if (refusal !== null) {
        setLocationRefusal(refusal);
        return;
      }
      if (isDefaultLocationInUse(error)) {
        setLocationFormError(
          t(
            "inventory.warehouses.defaultLocationInUse",
            "That bin is still the warehouse default and cannot be retired.",
          ),
        );
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const deactivateLocationMutation = useMutation({
    // List rows carry no per-row validator: read the exact bin immediately
    // before the write and forward that resource-bound ETag.
    mutationFn: async (input: { warehouseId: string; locationId: string }) => {
      const { etag } = await getLocation(input.warehouseId, input.locationId);
      if (etag === null) {
        throw new ApiError("Missing location ETag; reload and retry", 428);
      }
      return deactivateLocation(input.warehouseId, input.locationId, etag);
    },
    onSuccess: () => {
      setLocationRefusal(null);
      resetLocationForm();
      invalidateWarehouses();
    },
    onError: (error) => {
      if (handleWriteError(error)) return;
      const refusal = asLocationHasStockProblem(error);
      if (refusal !== null) {
        setLocationRefusal(refusal);
        return;
      }
      if (isDefaultLocationInUse(error)) {
        setLocationFormError(
          t(
            "inventory.warehouses.defaultLocationInUse",
            "That bin is still the warehouse default and cannot be retired.",
          ),
        );
        return;
      }
      surfaceProblem(error, t);
    },
  });

  // ── Submit handlers ────────────────────────────────────────────────────

  const submitCreateWarehouse = () => {
    const branchId = warehouseDraft.branchId ?? actingBranchId;
    if (
      warehouseDraft.code.trim() === "" ||
      warehouseDraft.name.trim() === "" ||
      warehouseDraft.defaultLocationCode.trim() === "" ||
      warehouseDraft.defaultLocationName.trim() === "" ||
      branchId === null
    ) {
      setWarehouseFormError(
        t(
          "inventory.warehouses.createRequired",
          "Code, name, owning branch and the default bin are all required.",
        ),
      );
      return;
    }
    setWarehouseFormError(null);
    createWarehouseMutation.mutate({ ...warehouseDraft, branchId });
  };

  const saveWarehouse = (patch: Parameters<typeof updateWarehouse>[1]) => {
    if (selectedId === null) return;
    if (warehouseEtag === null) {
      setWarehouseFormError(
        t("inventory.warehouses.reloadFirst", "Reload the warehouse before saving."),
      );
      return;
    }
    updateWarehouseMutation.mutate({ warehouseId: selectedId, patch, ifMatch: warehouseEtag });
  };

  const submitLocation = () => {
    if (selectedId === null) return;
    if (locationDraft.code.trim() === "" || locationDraft.name.trim() === "") {
      setLocationFormError(
        t("inventory.warehouses.binRequired", "Bin code and name are required."),
      );
      return;
    }
    setLocationFormError(null);
    if (editingLocationId === null) {
      createLocationMutation.mutate({ warehouseId: selectedId, draft: locationDraft });
      return;
    }
    if (editingLocationEtag === null) {
      setLocationFormError(t("inventory.warehouses.reloadBin", "Reload the bin before saving."));
      return;
    }
    updateLocationMutation.mutate({
      warehouseId: selectedId,
      locationId: editingLocationId,
      ifMatch: editingLocationEtag,
      patch: {
        name: locationDraft.name,
        location_type: locationDraft.locationType,
        status: locationDraft.status,
      },
    });
  };

  // ── Columns ────────────────────────────────────────────────────────────

  const warehouseColumns = useMemo<ColumnDef<Warehouse>[]>(
    () => [
      {
        accessorKey: "code",
        id: "code",
        header: t("inventory.warehouses.columns.code", "Code"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono font-medium">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "name",
        id: "name",
        header: t("inventory.warehouses.columns.name", "Name"),
        enableSorting: false,
        // A warehouse may carry an Arabic name; the reader's language decides
        // which one shows, and a warehouse without one keeps its Latin name.
        cell: (ctx) =>
          textCell(localizedName(ctx.row.original.name, ctx.row.original.name_ar, i18n.language)),
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("inventory.warehouses.columns.status", "Status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(
            value,
            { active: "success", archived: "secondary" },
            t(`inventory.warehouseStatus.${value}`, value),
          );
        },
      },
    ],
    [t, i18n.language],
  );

  const locationColumns = useMemo<ColumnDef<WarehouseLocation>[]>(
    () => [
      {
        accessorKey: "code",
        id: "code",
        header: t("inventory.warehouses.columns.binCode", "Bin"),
        enableSorting: false,
        cell: (ctx) => {
          const row = ctx.row.original;
          return (
            <span className="flex items-center gap-2">
              <span dir="ltr" className="font-mono font-medium">
                {row.code}
              </span>
              {warehouse?.default_location_id === row.id ? (
                <Badge variant="outline">{t("inventory.warehouses.default", "Default")}</Badge>
              ) : null}
            </span>
          );
        },
      },
      {
        accessorKey: "name",
        id: "name",
        header: t("inventory.warehouses.columns.name", "Name"),
        enableSorting: false,
        cell: (ctx) => textCell(ctx.getValue<string>()),
      },
      {
        accessorKey: "location_type",
        id: "location_type",
        header: t("inventory.warehouses.columns.binType", "Type"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(
            value,
            { pick: "default", bulk: "secondary", receiving: "outline", transit: "warning" },
            t(`inventory.locationType.${value}`, value),
          );
        },
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("inventory.warehouses.columns.status", "Status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(
            value,
            { active: "success", inactive: "secondary" },
            t(`inventory.locationStatus.${value}`, value),
          );
        },
      },
      {
        id: "actions",
        header: t("inventory.common.actions", "Actions"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) => {
          const row = ctx.row.original;
          if (selectedId === null) return null;
          return (
            <span className="flex justify-end gap-1">
              {canUpdate ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t("inventory.warehouses.editBin", "Edit bin {{code}}", {
                    code: row.code,
                  })}
                  disabled={loadLocationMutation.isPending}
                  onClick={() =>
                    loadLocationMutation.mutate({
                      warehouseId: selectedId,
                      locationId: row.id,
                    })
                  }
                >
                  <Pencil className="size-4" aria-hidden="true" />
                </Button>
              ) : null}
              {canDelete && row.status === "active" ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t("inventory.warehouses.deactivateBin", "Deactivate bin {{code}}", {
                    code: row.code,
                  })}
                  disabled={deactivateLocationMutation.isPending}
                  onClick={() =>
                    deactivateLocationMutation.mutate({
                      warehouseId: selectedId,
                      locationId: row.id,
                    })
                  }
                >
                  <Archive className="size-4" aria-hidden="true" />
                </Button>
              ) : null}
            </span>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- action cells derive from the mutation/permission state above
    [
      t,
      warehouse,
      selectedId,
      canUpdate,
      canDelete,
      loadLocationMutation.isPending,
      deactivateLocationMutation.isPending,
    ],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            {t("inventory.warehouses.title", "Warehouses & bins")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "inventory.warehouses.subtitle",
              "Stock locations, their bins and the default bin every posting falls back to.",
            )}
          </p>
        </div>
        {canCreate ? (
          <Button
            type="button"
            onClick={() => {
              setCreating(true);
              setWarehouseDraft({ ...EMPTY_WAREHOUSE, branchId: actingBranchId });
              setWarehouseFormError(null);
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("inventory.warehouses.new", "New warehouse")}
          </Button>
        ) : null}
      </div>

      {creating ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("inventory.warehouses.newTitle", "New warehouse")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="warehouse-code">{t("inventory.warehouses.code", "Code")}</Label>
              <Input
                id="warehouse-code"
                dir="ltr"
                value={warehouseDraft.code}
                onChange={(event) =>
                  setWarehouseDraft((current) => ({ ...current, code: event.target.value }))
                }
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  "inventory.warehouses.codeHint",
                  "Stored exactly as typed — no trimming or case conversion.",
                )}
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="warehouse-name">{t("inventory.warehouses.name", "Name")}</Label>
              <Input
                id="warehouse-name"
                value={warehouseDraft.name}
                onChange={(event) =>
                  setWarehouseDraft((current) => ({ ...current, name: event.target.value }))
                }
              />
            </div>
            {canReadBranches ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="warehouse-branch">{t("inventory.common.branch", "Branch")}</Label>
                <BranchSelect
                  id="warehouse-branch"
                  value={warehouseDraft.branchId}
                  canRead={canReadBranches}
                  onChange={(branchId) =>
                    setWarehouseDraft((current) => ({ ...current, branchId }))
                  }
                />
              </div>
            ) : null}
            <div className="flex items-center gap-2 sm:col-span-2">
              <Checkbox
                id="warehouse-locations-enabled"
                checked={warehouseDraft.locationsEnabled}
                onCheckedChange={(checked) =>
                  setWarehouseDraft((current) => ({
                    ...current,
                    locationsEnabled: checked === true,
                  }))
                }
              />
              <Label htmlFor="warehouse-locations-enabled">
                {t("inventory.warehouses.locationsEnabled", "Track bins in this warehouse")}
              </Label>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="warehouse-default-code">
                {t("inventory.warehouses.defaultBinCode", "Default bin code")}
              </Label>
              <Input
                id="warehouse-default-code"
                dir="ltr"
                value={warehouseDraft.defaultLocationCode}
                onChange={(event) =>
                  setWarehouseDraft((current) => ({
                    ...current,
                    defaultLocationCode: event.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="warehouse-default-name">
                {t("inventory.warehouses.defaultBinName", "Default bin name")}
              </Label>
              <Input
                id="warehouse-default-name"
                value={warehouseDraft.defaultLocationName}
                onChange={(event) =>
                  setWarehouseDraft((current) => ({
                    ...current,
                    defaultLocationName: event.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="warehouse-default-type">
                {t("inventory.warehouses.defaultBinType", "Default bin type")}
              </Label>
              <Select
                value={warehouseDraft.defaultLocationType}
                onValueChange={(value) =>
                  setWarehouseDraft((current) => ({
                    ...current,
                    defaultLocationType: value as WarehouseLocationType,
                  }))
                }
              >
                <SelectTrigger id="warehouse-default-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOCATION_TYPES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`inventory.locationType.${value}`, value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
              <Button
                type="button"
                loading={createWarehouseMutation.isPending}
                onClick={submitCreateWarehouse}
              >
                {t("inventory.warehouses.create", "Create warehouse")}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
                {t("actions.cancel")}
              </Button>
            </div>
            {warehouseFormError !== null ? (
              <p role="alert" className="text-sm text-destructive sm:col-span-2">
                {warehouseFormError}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(18rem,26rem)_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("inventory.warehouses.listTitle", "Warehouses")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="warehouse-search">
                  {t("inventory.warehouses.search", "Search")}
                </Label>
                <Input
                  id="warehouse-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t("inventory.warehouses.searchPlaceholder", "Code or name…")}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="warehouse-status-filter">
                  {t("inventory.common.status", "Status")}
                </Label>
                <Select
                  value={status}
                  onValueChange={(value) => setStatus(value as WarehouseStatus | typeof ALL)}
                >
                  <SelectTrigger id="warehouse-status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>{t("inventory.common.all", "All")}</SelectItem>
                    {WAREHOUSE_STATUSES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`inventory.warehouseStatus.${value}`, value)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DataTable
              columns={warehouseColumns}
              query={warehousesQuery}
              getRowId={(row) => row.id}
              onRowClick={(row) => {
                setSelectedId(row.id);
                resetLocationForm();
                setArchiveRefusal(null);
                setLocationRefusal(null);
              }}
              showColumnsMenu={false}
              emptyMessage={t("inventory.warehouses.empty", "No warehouses yet.")}
              errorMessage={t("inventory.warehouses.loadError", "Warehouses could not be loaded.")}
            />
          </CardContent>
        </Card>

        <div className="flex flex-col gap-4">
          {warehouse === null ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-2 p-10 text-center">
                <Building2 className="size-8 text-muted-foreground" aria-hidden="true" />
                <p className="text-sm text-muted-foreground">
                  {t(
                    "inventory.warehouses.selectPrompt",
                    "Choose a warehouse to see its bins and settings.",
                  )}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
                  <CardTitle>
                    <h2 className="flex flex-wrap items-center gap-2">
                      <span dir="ltr" className="font-mono">
                        {warehouse.code}
                      </span>
                      <span>{warehouse.name}</span>
                      <Badge variant={warehouse.status === "active" ? "success" : "secondary"}>
                        {t(`inventory.warehouseStatus.${warehouse.status}`, warehouse.status)}
                      </Badge>
                    </h2>
                  </CardTitle>
                  {canDelete && warehouse.status === "active" ? (
                    <Button
                      type="button"
                      variant="outline"
                      loading={archiveWarehouseMutation.isPending}
                      onClick={() => {
                        if (warehouseEtag === null) return;
                        archiveWarehouseMutation.mutate({
                          warehouseId: warehouse.id,
                          ifMatch: warehouseEtag,
                        });
                      }}
                    >
                      <Archive className="size-4" aria-hidden="true" />
                      {t("inventory.warehouses.archive", "Archive")}
                    </Button>
                  ) : null}
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  {archiveRefusal !== null ? (
                    <div
                      role="alert"
                      className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
                    >
                      <p className="font-medium">
                        {t(
                          "inventory.problems.warehouseHasMovements",
                          "This warehouse cannot be archived",
                        )}
                      </p>
                      <p className="text-muted-foreground">
                        {t(
                          "inventory.problems.warehouseHasMovementsBody",
                          "{{count}} ledger movements already reference it. Inventory archives rather than deletes, and a warehouse with history stays queryable.",
                          { count: archiveRefusal.movement_count },
                        )}
                      </p>
                    </div>
                  ) : null}

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="warehouse-detail-name">
                        {t("inventory.warehouses.name", "Name")}
                      </Label>
                      <Input
                        id="warehouse-detail-name"
                        defaultValue={warehouse.name}
                        key={`${warehouse.id}-name`}
                        disabled={!canUpdate}
                        onBlur={(event) => {
                          if (!canUpdate || event.target.value === warehouse.name) return;
                          saveWarehouse({ name: event.target.value });
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="warehouse-detail-status">
                        {t("inventory.common.status", "Status")}
                      </Label>
                      <Select
                        value={warehouse.status}
                        disabled={!canUpdate}
                        onValueChange={(value) =>
                          saveWarehouse({ status: value as WarehouseStatus })
                        }
                      >
                        <SelectTrigger id="warehouse-detail-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {WAREHOUSE_STATUSES.map((value) => (
                            <SelectItem key={value} value={value}>
                              {t(`inventory.warehouseStatus.${value}`, value)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="warehouse-detail-default">
                        {t("inventory.warehouses.defaultBin", "Default bin")}
                      </Label>
                      <LocationSelect
                        id="warehouse-detail-default"
                        warehouseId={warehouse.id}
                        value={warehouse.default_location_id}
                        disabled={!canUpdate}
                        onChange={(locationId) => {
                          if (locationId === null) return;
                          saveWarehouse({ default_location_id: locationId });
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="warehouse-detail-locations"
                        checked={warehouse.locations_enabled}
                        disabled={!canUpdate}
                        onCheckedChange={(checked) =>
                          saveWarehouse({ locations_enabled: checked === true })
                        }
                      />
                      <Label htmlFor="warehouse-detail-locations">
                        {t("inventory.warehouses.locationsEnabled", "Track bins in this warehouse")}
                      </Label>
                    </div>
                  </div>
                  {warehouseFormError !== null ? (
                    <p role="alert" className="text-sm text-destructive">
                      {warehouseFormError}
                    </p>
                  ) : null}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>
                    <h2>{t("inventory.warehouses.binsTitle", "Bins")}</h2>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  {!warehouse.locations_enabled ? (
                    <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
                      {t(
                        "inventory.warehouses.binsDisabled",
                        "Bin tracking is off here: every posting lands on the default bin.",
                      )}
                    </p>
                  ) : null}

                  {locationRefusal !== null ? (
                    <div
                      role="alert"
                      className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
                    >
                      <p className="font-medium">
                        {t("inventory.problems.locationHasStock", "This bin still holds stock")}
                      </p>
                      <p className="text-muted-foreground">
                        {t(
                          "inventory.problems.locationHasStockBody",
                          "{{count}} stock positions are still on it. Move or issue them first, then retire the bin.",
                          { count: locationRefusal.stock_level_count },
                        )}
                      </p>
                      {onOpenStock !== undefined ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="self-start"
                          onClick={() =>
                            onOpenStock({
                              warehouseId: locationRefusal.warehouse_id,
                              locationId: locationRefusal.location_id,
                            })
                          }
                        >
                          {t("inventory.problems.viewStockInBin", "View stock in this bin")}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="bin-type-filter">
                        {t("inventory.warehouses.columns.binType", "Type")}
                      </Label>
                      <Select
                        value={locationTypeFilter}
                        onValueChange={(value) =>
                          setLocationTypeFilter(value as WarehouseLocationType | typeof ALL)
                        }
                      >
                        <SelectTrigger id="bin-type-filter">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ALL}>{t("inventory.common.all", "All")}</SelectItem>
                          {LOCATION_TYPES.map((value) => (
                            <SelectItem key={value} value={value}>
                              {t(`inventory.locationType.${value}`, value)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="bin-status-filter">
                        {t("inventory.common.status", "Status")}
                      </Label>
                      <Select
                        value={locationStatusFilter}
                        onValueChange={(value) =>
                          setLocationStatusFilter(value as WarehouseLocationStatus | typeof ALL)
                        }
                      >
                        <SelectTrigger id="bin-status-filter">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ALL}>{t("inventory.common.all", "All")}</SelectItem>
                          {LOCATION_STATUSES.map((value) => (
                            <SelectItem key={value} value={value}>
                              {t(`inventory.locationStatus.${value}`, value)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <DataTable
                    columns={locationColumns}
                    query={locationsQuery}
                    getRowId={(row) => row.id}
                    showColumnsMenu={false}
                    emptyMessage={t("inventory.warehouses.binsEmpty", "No bins match.")}
                    errorMessage={t("inventory.warehouses.binsError", "Bins could not be loaded.")}
                  />

                  {canUpdate ? (
                    <div className="grid grid-cols-1 gap-3 border-t pt-4 sm:grid-cols-2">
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="bin-code">
                          {t("inventory.warehouses.binCode", "Bin code")}
                        </Label>
                        <Input
                          id="bin-code"
                          dir="ltr"
                          value={locationDraft.code}
                          disabled={editingLocationId !== null}
                          onChange={(event) =>
                            setLocationDraft((current) => ({
                              ...current,
                              code: event.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="bin-name">
                          {t("inventory.warehouses.binName", "Bin name")}
                        </Label>
                        <Input
                          id="bin-name"
                          value={locationDraft.name}
                          onChange={(event) =>
                            setLocationDraft((current) => ({
                              ...current,
                              name: event.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="bin-type">
                          {t("inventory.warehouses.columns.binType", "Type")}
                        </Label>
                        <Select
                          value={locationDraft.locationType}
                          onValueChange={(value) =>
                            setLocationDraft((current) => ({
                              ...current,
                              locationType: value as WarehouseLocationType,
                            }))
                          }
                        >
                          <SelectTrigger id="bin-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LOCATION_TYPES.map((value) => (
                              <SelectItem key={value} value={value}>
                                {t(`inventory.locationType.${value}`, value)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {editingLocationId !== null ? (
                        <div className="flex flex-col gap-1.5">
                          <Label htmlFor="bin-status">
                            {t("inventory.common.status", "Status")}
                          </Label>
                          <Select
                            value={locationDraft.status}
                            onValueChange={(value) =>
                              setLocationDraft((current) => ({
                                ...current,
                                status: value as WarehouseLocationStatus,
                              }))
                            }
                          >
                            <SelectTrigger id="bin-status">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {LOCATION_STATUSES.map((value) => (
                                <SelectItem key={value} value={value}>
                                  {t(`inventory.locationStatus.${value}`, value)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : null}
                      <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
                        <Button
                          type="button"
                          loading={
                            createLocationMutation.isPending || updateLocationMutation.isPending
                          }
                          onClick={submitLocation}
                        >
                          {editingLocationId === null
                            ? t("inventory.warehouses.addBin", "Add bin")
                            : t("inventory.warehouses.saveBin", "Save bin")}
                        </Button>
                        {editingLocationId !== null ? (
                          <Button type="button" variant="ghost" onClick={resetLocationForm}>
                            {t("actions.cancel")}
                          </Button>
                        ) : null}
                      </div>
                      {locationFormError !== null ? (
                        <p role="alert" className="text-sm text-destructive sm:col-span-2">
                          {locationFormError}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      <ConflictDialog
        open={conflict}
        onReload={() => {
          setConflict(false);
          resetLocationForm();
          invalidateWarehouses();
        }}
        onDismiss={() => setConflict(false)}
      />
    </div>
  );
}
